/**
 * ConferenceManagement Component
 * 
 * Handles conference management functionality including:
 * - Displaying conferences in a table format
 * - Creating, editing, and deleting conferences
 * - Searching and filtering conferences
 * - File management for conferences
 * - Conference status management
 * 
 * @component
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
    fetchAllConferences, 
    createConference, 
    updateConference, 
    deleteConference, 
    removeConferenceFile,
    removeConferencePhoto,
    setHeaderPhoto,
    setBookletFile,
    updateConferenceFees
} from '../../services/api';
import ConferenceModal from './ConferenceModal';

const ConferenceManagement = () => {
    // State management for conferences
    const [conferences, setConferences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showConferenceModal, setShowConferenceModal] = useState(false);
    const [editingConference, setEditingConference] = useState(null);
    const [conferenceSearchTerm, setConferenceSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const LOCALE = 'en-GB';
    const DATE_FORMAT_OPTIONS = { timeZone: 'Etc/GMT' };

    // Conference form data
    const [conferenceFormData, setConferenceFormData] = useState({
        title: '',
        location: '',
        startDate: '',
        endDate: '',
        status: 'Active',
        description: '',
        introduction: '',
        venue: '',
        socialEvents: '',
        accommodation: '',
        keynoteSpeakers: '',
        programChairs: '',
        programHtml: '',
        sponsoringInstitutions: '',
        conferenceObjective: '',
        areasOfInterest: '',
        doctoralStudentTutorialsAndPresentations: '',
        paperAndThesisSubmissionGuidelines: '',
        paperAwards: '',
        registrationFee: '',
        furtherInformation: '',
        scientificCommittee: '',
        localOrganizingCommittee: '',
        files: [],
        photos: [],
        fees: {
            base: '',
            earlyBird: '',
            regular: '',
            late: '',
            earlyBirdDeadline: '',
            regularRegistrationDeadline: '',
            currency: 'EUR',
            exchangeRate: 1.1,
            doctoralStudentDiscount: 25,
            accompaniedPersonDiscount: 50,
            description: ''
        },
        registrationEnabled: true,
        paymentRequired: false,
        registrationDeadline: ''
    });

    // Available conference statuses
    const conferenceStatuses = ['Active', 'Open', 'Close', 'Ended'];

    // Load conferences when component mounts
    useEffect(() => {
        loadConferences();
    }, []);

    /**
     * Fetches all conferences from the API
     */
    const loadConferences = async () => {
        try {
            setLoading(true);
            const data = await fetchAllConferences();
            
            setConferences(data);
            setError('');
        } catch (error) {
            console.error("Error fetching conferences:", error);
            console.error("Error details:", error.response?.data);
            
            setError(`Failed to load conferences: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles conference form input changes
     * @param {Event} e - The input change event
     */
    const handleConferenceFormChange = (e) => {
        const { name, value, files, type } = e.target;
        let { checked } = e.target;
        if (type === 'checkbox' && checked === undefined) {
            // Derive fallback: invert current stored value
            const current = conferenceFormData[name];
            checked = !current;
            console.warn('[ConferenceManagement] Deriving checkbox state because checked was undefined:', name, 'derived =>', checked);
        }

        if (name === 'files' || name === 'photos') {
            setConferenceFormData(prev => ({
                ...prev,
                [name]: Array.from(files)
            }));
            return;
        }

        if (name.startsWith('fees.')) {
            const feesField = name.split('.')[1];
            setConferenceFormData(prev => ({
                ...prev,
                fees: {
                    ...prev.fees,
                    [feesField]: value
                }
            }));
            return;
        }

        if ([ 'registrationEnabled', 'paymentRequired' ].includes(name)) {
            if (type === 'checkbox') {
                setConferenceFormData(prev => ({ ...prev, [name]: !!checked }));
            } else {
                setConferenceFormData(prev => ({ ...prev, [name]: value === 'true' }));
            }
            return;
        }

        setConferenceFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    /**
     * Resets the conference form to default values
     */
    const resetConferenceForm = () => {
        setConferenceFormData({
            title: '',
            location: '',
            startDate: '',
            endDate: '',
            status: 'Active',
            description: '',
            introduction: '',
            venue: '',
            socialEvents: '',
            accommodation: '',
            keynoteSpeakers: '',
            programChairs: '',
            programHtml: '',
            sponsoringInstitutions: '',
            conferenceObjective: '',
            areasOfInterest: '',
            doctoralStudentTutorialsAndPresentations: '',
            paperAndThesisSubmissionGuidelines: '',
            paperAwards: '',
            registrationFee: '',
            furtherInformation: '',
            scientificCommittee: '',
            localOrganizingCommittee: '',
            files: [],
            photos: [],
            fees: {
                base: '',
                earlyBird: '',
                regular: '',
                late: '',
                earlyBirdDeadline: '',
                regularRegistrationDeadline: '',
                currency: 'EUR',
                exchangeRate: 1.1,
                doctoralStudentDiscount: 25,
                accompaniedPersonDiscount: 50,
                description: ''
            },
            registrationEnabled: true,
            paymentRequired: false,
            registrationDeadline: ''
        });
        setEditingConference(null);
    };

    /**
     * Handles conference form submission
     * @param {Event} e - The form submit event
     */
    const handleConferenceSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            
            // Append form fields
            Object.keys(conferenceFormData).forEach(key => {
                if (key === 'files') {
                    conferenceFormData.files.forEach(file => formData.append('files', file));
                    return;
                }
                if (key === 'photos') {
                    conferenceFormData.photos.forEach(photo => formData.append('photos', photo));
                    return;
                }
                if (key === 'fees') {
                    // Only append defined fee fields
                    const { fees } = conferenceFormData;
                    if (fees.base !== '') formData.append('fees.base', fees.base);
                    if (fees.earlyBird !== '') formData.append('fees.earlyBird', fees.earlyBird);
                    if (fees.regular !== '') formData.append('fees.regular', fees.regular);
                    if (fees.late !== '') formData.append('fees.late', fees.late);
                    if (fees.earlyBirdDeadline) formData.append('fees.earlyBirdDeadline', fees.earlyBirdDeadline);
                    if (fees.regularRegistrationDeadline) formData.append('fees.regularRegistrationDeadline', fees.regularRegistrationDeadline);
                    if (fees.currency) formData.append('fees.currency', fees.currency);
                    if (fees.exchangeRate !== '' && fees.exchangeRate !== undefined) formData.append('fees.exchangeRate', fees.exchangeRate);
                    if (fees.doctoralStudentDiscount !== '' && fees.doctoralStudentDiscount !== undefined) formData.append('fees.doctoralStudentDiscount', fees.doctoralStudentDiscount);
                    if (fees.accompaniedPersonDiscount !== '' && fees.accompaniedPersonDiscount !== undefined) formData.append('fees.accompaniedPersonDiscount', fees.accompaniedPersonDiscount);
                    if (fees.description !== '') formData.append('fees.description', fees.description);
                    return;
                }
                if (['registrationEnabled', 'paymentRequired'].includes(key)) {
                    // Always append even if false
                    formData.append(key, conferenceFormData[key] ? 'true' : 'false');
                    return;
                }
                if (key === 'registrationDeadline') {
                    if (conferenceFormData.registrationDeadline) {
                        formData.append('registrationDeadline', conferenceFormData.registrationDeadline);
                    } else {
                        // Explicitly clear by sending empty if previously set
                        formData.append('registrationDeadline', '');
                    }
                    return;
                }
                formData.append(key, conferenceFormData[key]);
            });

            let savedConference;

            if (editingConference) {
                // Update existing conference
                const response = await updateConference(editingConference._id, formData);
                savedConference = response?.conference;
                alert('Conference updated successfully!');
            } else {
                // Create new conference
                const response = await createConference(formData);
                savedConference = response?.conference;
                alert('Conference created successfully!');
            }

            if (savedConference?._id) {
                setConferences(prevConferences => {
                    const existingIndex = prevConferences.findIndex(conf => conf._id === savedConference._id);
                    if (existingIndex === -1) {
                        return [savedConference, ...prevConferences];
                    }
                    const nextConferences = [...prevConferences];
                    nextConferences[existingIndex] = savedConference;
                    return nextConferences;
                });
                setEditingConference(savedConference);
            }

            setShowConferenceModal(false);
            resetConferenceForm();
            loadConferences();
        } catch (error) {
            console.error('Error saving conference:', error);
            alert('Failed to save conference');
        }
    };

    /**
     * Prepares conference for editing
     * @param {Object} conference - The conference to edit
     */
    const handleEditConference = (conference) => {
        setEditingConference(conference);
        setConferenceFormData({
            title: conference.title || '',
            location: conference.location || '',
            startDate: conference.startDate ? conference.startDate.split('T')[0] : '',
            endDate: conference.endDate ? conference.endDate.split('T')[0] : '',
            status: conference.status || 'Active',
            description: conference.description || '',
            introduction: conference.introduction || '',
            venue: conference.venue || '',
            socialEvents: conference.socialEvents || '',
            accommodation: conference.accommodation || '',
            keynoteSpeakers: conference.keynoteSpeakers || '',
            programChairs: conference.programChairs || '',
            programHtml: conference.programHtml || '',
            sponsoringInstitutions: conference.sponsoringInstitutions || '',
            conferenceObjective: conference.conferenceObjective || '',
            areasOfInterest: conference.areasOfInterest || '',
            doctoralStudentTutorialsAndPresentations: conference.doctoralStudentTutorialsAndPresentations || '',
            paperAndThesisSubmissionGuidelines: conference.paperAndThesisSubmissionGuidelines || '',
            paperAwards: conference.paperAwards || '',
            registrationFee: conference.registrationFee || '',
            furtherInformation: conference.furtherInformation || '',
            scientificCommittee: conference.scientificCommittee || '',
            localOrganizingCommittee: conference.localOrganizingCommittee || '',
            files: [],
            photos: [],
            fees: {
                base: conference.fees?.base ?? '',
                earlyBird: conference.fees?.earlyBird ?? '',
                regular: conference.fees?.regular ?? '',
                late: conference.fees?.late ?? '',
                earlyBirdDeadline: conference.fees?.earlyBirdDeadline
                    ? conference.fees.earlyBirdDeadline.split('T')[0]
                    : '',
                regularRegistrationDeadline: conference.fees?.regularRegistrationDeadline
                    ? conference.fees.regularRegistrationDeadline.split('T')[0]
                    : '',
                currency: conference.fees?.currency || 'EUR',
                exchangeRate: conference.fees?.exchangeRate ?? 1.1,
                doctoralStudentDiscount: conference.fees?.doctoralStudentDiscount ?? 25,
                accompaniedPersonDiscount: conference.fees?.accompaniedPersonDiscount ?? 50,
                description: conference.fees?.description || ''
            },
            registrationEnabled: conference.registrationEnabled !== undefined ? conference.registrationEnabled : true,
            paymentRequired: conference.paymentRequired !== undefined ? conference.paymentRequired : false,
            registrationDeadline: conference.registrationDeadline ? conference.registrationDeadline.split('T')[0] : ''
        });
        setShowConferenceModal(true);
    };

    /**
     * Deletes a conference after confirmation
     * @param {string} conferenceId - The ID of the conference to delete
     */
    const handleDeleteConference = async (conferenceId) => {
        if (window.confirm('Are you sure you want to delete this conference? This action cannot be undone.')) {
            try {
                await deleteConference(conferenceId);
                setConferences(conferences.filter(conf => conf._id !== conferenceId));
                alert('Conference deleted successfully');
            } catch (error) {
                console.error("Error deleting conference:", error);
                alert('Failed to delete conference');
            }
        }
    };

    /**
     * Gets the appropriate badge color for a conference status
     * @param {string} status - The conference status
     * @returns {string} Bootstrap badge class
     */
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Active': return 'bg-success';
            case 'Open': return 'bg-primary';
            case 'Close': return 'bg-warning';
            case 'Ended': return 'bg-secondary';
            default: return 'bg-secondary';
        }
    };

    /**
     * Gets the appropriate icon for a file type
     * @param {string} mimetype - The file MIME type
     * @returns {string} Bootstrap icon class
     */
    const getFileIcon = (mimetype) => {
        if (mimetype?.includes('pdf')) return 'bi-file-earmark-pdf';
        if (mimetype?.includes('image')) return 'bi-file-earmark-image';
        if (mimetype?.includes('word') || mimetype?.includes('document')) return 'bi-file-earmark-word';
        if (mimetype?.includes('excel') || mimetype?.includes('spreadsheet')) return 'bi-file-earmark-excel';
        return 'bi-file-earmark';
    };

    /**
     * Deletes a conference file
     * @param {string} conferenceId - The ID of the conference
     * @param {string} filename - The filename to delete
     */
    const handleDeleteFile = async (conferenceId, filename) => {
        if (window.confirm('Are you sure you want to delete this file?')) {
            try {
                await removeConferenceFile(conferenceId, filename);
                
                // Update the conferences list
                await loadConferences();
                
                // If we're editing this conference, update the editing state immediately
                if (editingConference && editingConference._id === conferenceId) {
                    const updatedConferences = await fetchAllConferences();
                    const updatedConference = updatedConferences.find(c => c._id === conferenceId);
                    if (updatedConference) {
                        setEditingConference(updatedConference);
                    }
                }
                
                alert('File deleted successfully');
            } catch (error) {
                console.error('Error deleting file:', error);
                alert('Failed to delete file');
            }
        }
    };

    /**
     * Deletes a conference photo
     * @param {string} conferenceId - The ID of the conference
     * @param {string} filename - The filename to delete
     */
    const handleDeletePhoto = async (conferenceId, filename) => {
        if (window.confirm('Are you sure you want to delete this photo?')) {
            try {
                await removeConferencePhoto(conferenceId, filename);
                
                // Update the conferences list
                await loadConferences();
                
                // If we're editing this conference, update the editing state immediately
                if (editingConference && editingConference._id === conferenceId) {
                    const updatedConferences = await fetchAllConferences();
                    const updatedConference = updatedConferences.find(c => c._id === conferenceId);
                    if (updatedConference) {
                        setEditingConference(updatedConference);
                    }
                }
                
                alert('Photo deleted successfully');
            } catch (error) {
                console.error('Error deleting photo:', error);
                alert('Failed to delete photo');
            }
        }
    };

    /**
     * Sets a photo as the header photo for a conference
     * @param {string} conferenceId - The ID of the conference
     * @param {string} filename - The filename to set as header
     */
    const handleSetHeaderPhoto = async (conferenceId, filename) => {
        try {
            await setHeaderPhoto(conferenceId, filename);
            
            // Update the conferences list
            await loadConferences();
            
            // If we're editing this conference, update the editing state immediately
            if (editingConference && editingConference._id === conferenceId) {
                const updatedConferences = await fetchAllConferences();
                const updatedConference = updatedConferences.find(c => c._id === conferenceId);
                if (updatedConference) {
                    setEditingConference(updatedConference);
                }
            }
            
            alert('Header photo set successfully');
        } catch (error) {
            console.error('Error setting header photo:', error);
            alert('Failed to set header photo');
        }
    };

    /**
     * Marks a single conference file as the official booklet
     * @param {string} conferenceId - The ID of the conference
     * @param {string} filename - The filename to mark as booklet
     */
    const handleSetBookletFile = async (conferenceId, filename) => {
        try {
            await setBookletFile(conferenceId, filename);

            await loadConferences();

            if (editingConference && editingConference._id === conferenceId) {
                const updatedConferences = await fetchAllConferences();
                const updatedConference = updatedConferences.find(c => c._id === conferenceId);
                if (updatedConference) {
                    setEditingConference(updatedConference);
                }
            }

            alert('Booklet selection updated');
        } catch (error) {
            console.error('Error setting booklet file:', error);
            alert('Failed to update booklet selection');
        }
    };

    /**
     * Updates conference fees
     * @param {string} conferenceId - The ID of the conference
     * @param {Object} fees - The fees object with amount, currency, and description
     */
    const handleUpdateFees = async (conferenceId, fees) => {
        try {
            await updateConferenceFees(conferenceId, fees);
            
            // Update the conferences list
            await loadConferences();
            
            // If we're editing this conference, update the editing state immediately
            if (editingConference && editingConference._id === conferenceId) {
                const updatedConferences = await fetchAllConferences();
                const updatedConference = updatedConferences.find(c => c._id === conferenceId);
                if (updatedConference) {
                    setEditingConference(updatedConference);
                    // Update form data to reflect the changes
                    setConferenceFormData(prev => ({
                        ...prev,
                        fees: fees
                    }));
                }
            }
            
            alert('Conference fees updated successfully');
        } catch (error) {
            console.error('Error updating fees:', error);
            alert('Failed to update fees');
        }
    };

    /**
     * Filters conferences based on search term and status filter
     */
    const filteredConferences = conferences.filter(conference => {
        const matchesSearch = 
            conference.title?.toLowerCase().includes(conferenceSearchTerm.toLowerCase()) ||
            conference.location?.toLowerCase().includes(conferenceSearchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === 'all' || conference.status === filterStatus;
        
        return matchesSearch && matchesStatus;
    });

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="mb-0">
                            <i className="bi bi-calendar-event me-2"></i>
                            Conference Management
                        </h3>
                        <div className="d-flex align-items-center gap-3">
                            <span className="badge bg-info">{filteredConferences.length} conferences</span>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    resetConferenceForm();
                                    setShowConferenceModal(true);
                                }}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                Add Conference
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {/* Search and Filter Controls */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search conferences..."
                                    value={conferenceSearchTerm}
                                    onChange={(e) => setConferenceSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                {conferenceStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <button
                                className="btn btn-outline-secondary w-100"
                                onClick={loadConferences}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Conferences Table */}
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Conference</th>
                                        <th>Location</th>
                                        <th>Dates</th>
                                        <th>Status</th>
                                        <th>Files</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredConferences.map((conference) => (
                                        <tr key={conference._id}>
                                            <td>
                                                <div>
                                                    <strong>{conference.title}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <small>{conference.location}</small>
                                            </td>
                                            <td>
                                                <div>
                                                    <small>
                                                        <strong>Start:</strong> {new Date(conference.startDate).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}
                                                    </small>
                                                    <br />
                                                    <small>
                                                        <strong>End:</strong> {new Date(conference.endDate).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadgeColor(conference.status)}`}>
                                                    {conference.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div>
                                                    {conference.files && conference.files.length > 0 && conference.photos && conference.photos.length > 0 ? (
                                                        <div>
                                                            <span className="badge bg-info me-1">
                                                                {conference.files.length} file(s)
                                                            </span>
                                                            <span className="badge bg-success">
                                                                {conference.photos.length} photo(s)
                                                            </span>
                                                        </div>
                                                    ) : conference.files && conference.files.length > 0 ? (
                                                        <div>
                                                            <span className="badge bg-info">
                                                                {conference.files.length} file(s)
                                                            </span>
                                                        </div>
                                                    ) : conference.photos && conference.photos.length > 0 ? (
                                                        <div>
                                                            <span className="badge bg-success">
                                                                {conference.photos.length} photo(s)
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">No files</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm">
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => handleEditConference(conference)}
                                                        title="Edit Conference"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger"
                                                        onClick={() => handleDeleteConference(conference._id)}
                                                        title="Delete Conference"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {filteredConferences.length === 0 && (
                                <div className="text-center py-4">
                                    <i className="bi bi-calendar-x" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                    <p className="text-muted mt-2">
                                        {conferenceSearchTerm || filterStatus !== 'all' ? 'No conferences match your search criteria.' : 'No conferences found.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Conference Modal */}
            {showConferenceModal && (
                <ConferenceModal
                    show={showConferenceModal}
                    onHide={() => setShowConferenceModal(false)}
                    editingConference={editingConference}
                    conferenceFormData={conferenceFormData}
                    onFormChange={handleConferenceFormChange}
                    onSubmit={handleConferenceSubmit}
                    onReset={resetConferenceForm}
                    conferenceStatuses={conferenceStatuses}
                    getFileIcon={getFileIcon}
                    onDeleteFile={handleDeleteFile}
                    onDeletePhoto={handleDeletePhoto}
                    onSetHeaderPhoto={handleSetHeaderPhoto}
                    onSetBookletFile={handleSetBookletFile}
                    onUpdateFees={handleUpdateFees}
                />
            )}
        </>
    );
};

export default ConferenceManagement;
